import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { get } from "../lib/api";
import { useDebounced, useDocumentTitle, useLoad, useQueryState, useOnChange, useRememberListUrl } from "../lib/hooks";
import { COMPANY_STATUSES, cadenceText, optionLabel, scoreColor } from "../lib/format";
import { Avatar, Badge, Button, Card, DueBadge, Empty, ErrorBox, Loading, PageHead, Pager, ScoreBar, SearchInput, Select, Tabs } from "../ui";
import { LastTouchCell, RowActions, SaveViewButton, useSavedViews } from "../components/ListParts";
import { COMPANY_SORTS, companyListParams, defaultSort, dueViewCounts } from "../lib/lists";
import { useShell } from "../shell/context";

const PAGE_SIZE = 50;

const BUILTIN = [
    { key: "all", label: "All" },
    { key: "kept", label: "Keep in touch" },
    { key: "overdue", label: "Overdue" },
    { key: "due_soon", label: "Due soon" },
    { key: "snoozed", label: "Snoozed" },
];

export const STATUS_COLOR = { prospect: "secondary", target: "info", active_client: "success", past_client: "warning", partner: "primary", inactive: "secondary" };

export default function Companies() {
    useDocumentTitle("Companies");
    useRememberListUrl();
    const { version, bump, openCreate } = useShell();
    const saved = useSavedViews("companies");
    const [view, setView] = useQueryState("view", "all");
    const [q, setQ] = useQueryState("q", "");
    const [status, setStatus] = useQueryState("status", "");
    const [tag, setTag] = useQueryState("tag", "");
    const [sort, setSort] = useQueryState("sort", "");
    const [page, setPage] = useQueryState("page", 1);
    const dq = useDebounced(q, 250);

    const custom = saved.views.find((v) => v.id === view);
    const base = custom ? custom.view : view;

    useOnChange([dq, status, tag, sort, view], () => setPage(1));

    const tags = useLoad(() => get("v1/crm/tags"), []);
    const counts = useLoad(() => dueViewCounts("v1/crm/companies"), [version]);

    // Every view, filter and sort runs on the server (API polish), paged.
    const list = useLoad(
        () => get("v1/crm/companies", companyListParams({ view: base, q: dq, status, tag, sort, page, pageSize: PAGE_SIZE })),
        [base, dq, status, tag, sort, page, version]
    );
    const rows = (list.data && list.data.data) || [];

    const pickView = (key) => {
        setView(key);
        const v = saved.views.find((x) => x.id === key);
        if (v) {
            setQ(v.q || "");
            setStatus(v.status || "");
            setTag(v.tag || "");
            setSort(v.sort || "");
        } else {
            setSort("");
        }
    };

    const c = counts.data || {};
    const tabs = [
        ...BUILTIN.map((b) => ({
            key: b.key,
            label: b.label,
            count: b.key === "overdue" ? c.overdue : b.key === "due_soon" ? c.due_soon : b.key === "snoozed" ? c.snoozed : b.key === view && list.data ? list.data.count : undefined,
            danger: b.key === "overdue",
        })),
        ...saved.views.map((v) => ({ key: v.id, label: v.name, onRemove: () => { saved.remove(v.id); if (view === v.id) setView("all"); } })),
    ];

    return (
        <>
            <PageHead
                crumb="CRM"
                title="Companies"
                sub={list.data ? `${list.data.count} ${list.data.count === 1 ? "company" : "companies"} in this view` : " "}
                actions={<Button variant="primary" icon="ri-add-line" onClick={() => openCreate("company")}>New company</Button>}
            />
            <Card noBody>
                <Tabs tabs={tabs} active={view} onChange={pickView} />
                <div className="hz-filterbar">
                    <SearchInput value={q} onChange={setQ} placeholder="Filter by name or domain…" />
                    <Select value={status} onChange={(e) => setStatus(e.target.value)} placeholder="Any status" options={COMPANY_STATUSES} />
                    <Select value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Any tag" options={((tags.data && tags.data.data) || []).map((t) => ({ value: t.name, label: `${t.name} (${t.usage_count})` }))} />
                    <Select
                        value={sort || defaultSort(base)}
                        onChange={(e) => setSort(e.target.value)}
                        title="Sort"
                        options={COMPANY_SORTS.map((o) => ({ value: o.value, label: `Sort: ${o.label}` }))}
                    />
                    <SaveViewButton onSave={(name) => saved.add({ name, view: base, q, status, tag, sort })} />
                </div>
                <ErrorBox error={list.error} onRetry={list.reload} />
                {list.loading && !list.data ? (
                    <Loading />
                ) : (
                    <div className="hz-table-wrap">
                        <table className="hz-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Status</th>
                                    <th>Score</th>
                                    <th>Due</th>
                                    <th>Last touch</th>
                                    <th>People</th>
                                    <th>Open jobs</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className={r.due_status === "overdue" ? "overdue" : ""}>
                                        <td>
                                            <Link to={`/crm/companies/${r.id}`} className="hz-entity-cell">
                                                <Avatar name={r.name} />
                                                <span style={{ minWidth: 0 }}>
                                                    <span className="hz-entity-name">{r.name}</span>
                                                    <span className="hz-entity-sub">{[r.domain, r.industry, r.country].filter(Boolean).join(" · ")}</span>
                                                </span>
                                            </Link>
                                            {(r.tags || []).length > 0 && (
                                                <div className="hz-row-wrap" style={{ gap: 4, marginTop: 4, paddingLeft: 42 }}>
                                                    {r.tags.map((t) => <span key={t} className="hz-tag">{t}</span>)}
                                                </div>
                                            )}
                                        </td>
                                        <td><Badge color={STATUS_COLOR[r.status] || "secondary"}>{optionLabel(COMPANY_STATUSES, r.status)}</Badge></td>
                                        <td>
                                            <div className="hz-row" style={{ gap: 8 }}>
                                                <ScoreBar value={r.qualification_score || 0} color={scoreColor(r.qualification_score || 0)} />
                                                <span className="hz-strong">{r.qualification_score || 0}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <DueBadge status={r.due_status} dueInDays={r.due_in_days} snoozedUntil={r.snoozed_until} withText />
                                            {r.keep_in_touch && <div className="hz-xs hz-muted">{cadenceText(r)}</div>}
                                        </td>
                                        <td><LastTouchCell date={r.last_touch_at} /></td>
                                        <td>{r.contact_count || 0}</td>
                                        <td>{r.open_job_count > 0 ? <Badge color="success" soft={false}>{r.open_job_count}</Badge> : <span className="hz-muted">0</span>}</td>
                                        <td className="actions"><RowActions kind="company" row={r} onChanged={bump} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {rows.length === 0 && !list.error && <Empty>No companies match this view.</Empty>}
                    </div>
                )}
                {list.data && <Pager page={page} maxPage={list.data.max_page} count={list.data.count} onPage={setPage} label="companies" />}
            </Card>
        </>
    );
}
